import axios from 'axios';
import { BASE_URL } from './config';
import {
    getAccessToken,
    refreshSession,
    isTokenExpired
} from './token';

const AUTH_HEADER = 'Authorization';
const BEARER_PREFIX = 'Bearer ';

// Публичный экземпляр — без обработки 401 (login, register и т.д.)
export const publicApi = axios.create({
    baseURL: BASE_URL,
    headers: { 'Content-Type': 'application/json' }
});

// Основной экземпляр с авторизацией и интерсепторами
export const api = axios.create({
    baseURL: BASE_URL,
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true
});

let isRefreshing = false;
let subscribers = [];
const MAX_SUBSCRIBERS = 50;

const subscribeTokenRefresh = (cb) => {
    if (subscribers.length < MAX_SUBSCRIBERS) {
        subscribers.push(cb);
    }
};

const notifySubscribers = (token) => {
    subscribers.forEach((cb) => cb(token));
    subscribers = [];
};

// Request interceptor: добавляем токен, для FormData снимаем дефолтный JSON Content-Type
api.interceptors.request.use(
    (config) => {
        const token = getAccessToken();
        if (token && !isTokenExpired(token)) {
            config.headers.set(AUTH_HEADER, `${BEARER_PREFIX}${token}`);
        }
        if (config.data instanceof FormData) {
            config.headers.delete('Content-Type');
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor: обработка 429, 401 и обновление сессии
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (!originalRequest) return Promise.reject(error);

        if (originalRequest._retry || originalRequest.skipAuthRefresh) {
            return Promise.reject(error);
        }

        const status = error.response?.status;

        if (status === 429) {
            console.warn('[axios] 429 Too Many Requests');
            return Promise.reject(error);
        }

        if (status === 401) {
            if (!navigator.onLine) {
                window.dispatchEvent(new CustomEvent('network.offline'));
                return Promise.reject(error);
            }

            try {
                if (isRefreshing) {
                    return new Promise((resolve, reject) => {
                        subscribeTokenRefresh((token) => {
                            if (!token) {
                                reject(error);
                                return;
                            }
                            resolve(
                                api({
                                    ...originalRequest,
                                    headers: {
                                        ...originalRequest.headers,
                                        [AUTH_HEADER]: `${BEARER_PREFIX}${token}`
                                    }
                                })
                            );
                        });
                    });
                }

                originalRequest._retry = true;
                isRefreshing = true;

                console.log('[axios] Token expired, refreshing...');
                const newToken = await refreshSession();

                if (!newToken) {
                    console.error('[axios] Refresh returned no token');
                    throw new Error('Refresh returned no token');
                }

                console.log('[axios] Token refreshed successfully');
                isRefreshing = false;
                notifySubscribers(newToken);

                originalRequest.headers = {
                    ...originalRequest.headers,
                    [AUTH_HEADER]: `${BEARER_PREFIX}${newToken}`
                };
                return api(originalRequest);
            } catch (err) {
                isRefreshing = false;
                notifySubscribers(null);
                window.dispatchEvent(new CustomEvent('auth.unauthorized'));
                return Promise.reject(err);
            }
        }

        return Promise.reject(error);
    }
);
